import { useEffect } from 'react';

import styled from 'styled-components';

import { useLocalStorageState } from '@components/component-helpers';
import { useIsModalActive } from '@components/modal/modal';
import { ProgressWidget } from '@components/progress-widget/progress-widget';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useCurrentUser } from 'api/authentication/hooks';
import { useCompanyOnboardingSidebar } from 'ui/authenticated/sidebars/company-onboarding';

const SHOW_SIDEBAR_DELAY_MS = 500;

export const CompanyOnboarding = () => {
  const { t } = useScopedTranslate(locale);
  const currentUser = useCurrentUser();
  const [companyOnboardingLocation, setCompanyOnboardingLocation] = useLocalStorageState<
    'sidebar' | 'progress-widget'
  >({
    key: `company-onboarding-location-${currentUser?.company.shortId}`,
    defaultValue: 'sidebar',
  });

  const isAnyModalActive = useIsModalActive();

  const shouldShowCompanyOnboarding =
    !isAnyModalActive &&
    currentUser?.company.onboarding &&
    !currentUser?.company.onboarding?.isSkipped &&
    !currentUser?.company.onboarding?.isCompleted;

  const showCompanyOnboardingSidebar = useCompanyOnboardingSidebar({
    onHide: () => setCompanyOnboardingLocation('progress-widget'),
  });

  useEffect(() => {
    if (!shouldShowCompanyOnboarding) return;
    if (companyOnboardingLocation !== 'sidebar') return;
    const timeout = setTimeout(() => {
      showCompanyOnboardingSidebar();
    }, SHOW_SIDEBAR_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [companyOnboardingLocation, shouldShowCompanyOnboarding, showCompanyOnboardingSidebar]);

  if (!shouldShowCompanyOnboarding || companyOnboardingLocation !== 'progress-widget') return null;

  return (
    <ProgressWidgetContainer onClick={() => setCompanyOnboardingLocation('sidebar')}>
      <ProgressWidget
        completedRatio={currentUser?.company?.onboarding?.ratio ?? 0}
        title={t('gettingStarted')}
      />
    </ProgressWidgetContainer>
  );
};

const ProgressWidgetContainer = styled.div({
  position: 'absolute',
  bottom: pxToRem(16),
  right: pxToRem(16),
  cursor: 'pointer',
  zIndex: 999,
});

const locale = {
  en: {
    gettingStarted: 'Getting started',
  },
};
